import {
  DefinitionTooltip,
  RadioButton,
  RadioButtonGroup,
} from '@carbon/react';
import { Information } from '@carbon/react/icons';
import style from './volume-display-mode-radio.module.scss';
import { DAILY_DIVISOR } from './volume-display-scaling';

/** Whether volumes are shown as monthly totals or daily rates. */
export type VolumeDisplayMode = 'monthly' | 'daily';

/** What the Daily option shows, for its tooltip. */
const DAILY_DEFINITION = `Daily production is shown as reported; monthly production is divided by ${DAILY_DIVISOR}`;

interface VolumeDisplayModeRadioProps {
  value: VolumeDisplayMode;
  onChange: (mode: VolumeDisplayMode) => void;
  /** Prefixes the radio group's name and the buttons' ids, unique on the page. */
  idPrefix: string;
}

/**
 * The Monthly / Daily choice for how a DCA chart shows volumes.
 *
 * @param {VolumeDisplayModeRadioProps} props - The selected mode, the change handler and the id prefix.
 * @return {JSX.Element} The radio group.
 */
export function VolumeDisplayModeRadio({
  value,
  onChange,
  idPrefix,
}: VolumeDisplayModeRadioProps) {
  return (
    <div className={style.volumeDisplayRadioContainer}>
      <RadioButtonGroup
        legendText="Volume display"
        name={`${idPrefix}-volume-display-mode`}
        valueSelected={value}
        onChange={(mode: string | number | undefined) =>
          onChange(mode as VolumeDisplayMode)
        }
      >
        <RadioButton
          labelText="Monthly"
          value="monthly"
          id={`${idPrefix}-volume-display-monthly`}
        />
        <RadioButton
          labelText={
            <>
              Daily
              <DefinitionTooltip
                definition={DAILY_DEFINITION}
                align="bottom-end"
                className={style.dailyInfoTooltip}
                openOnHover
              >
                <Information />
              </DefinitionTooltip>
            </>
          }
          value="daily"
          id={`${idPrefix}-volume-display-daily`}
        />
      </RadioButtonGroup>
    </div>
  );
}
